import {
  DataType,
  DocumentQueryDto,
  DocumentQueryFieldDto,
  ExpressionDataFunction,
  ExpressionFunctionsCachedResult,
  ExpressionFunctionsDto,
} from '../types';
import { addMilliseconds, format } from 'date-fns';
import i18next from 'i18next';
import { initialData } from '../executor';
import { localFormat, StartDateForTimeOnlyType } from './date-helper';
import { cleanTextFromTags } from './tag-helper';
import { assertNeverDontThrow } from './locale';
export const NS_DataBlockFields = 'data-block-fields';

function postProcessAllDataBlockFields(
  pathPrefix: string,
  data: Record<string, any>,
  currentBaseQuery: DocumentQueryDto,
  postProcessFuncs: ((
    queryField: DocumentQueryFieldDto,
    data: Record<string, any>,
    key: string
  ) => void)[]
) {
  const keys = new Set([
    ...Object.keys(data),
    // we need to process all query fields to convert boolean fields to 'FALSE' if they are undefined
    ...(currentBaseQuery?.queryFields.map((x) => x.dataField) ?? []),
  ]);
  keys.forEach((key) => {
    const value = data[key];

    const fullPath = key; //keyToQueryDataField(pathPrefix, key);
    const queryField = currentBaseQuery?.queryFields.find(
      (x) => x.dataField === fullPath
    );
    if (queryField && queryField !== undefined) {
      postProcessFuncs.forEach((x) => x(queryField, data, key));
    } else if (
      value !== null &&
      value !== undefined &&
      value instanceof Object &&
      !Array.isArray(value)
    ) {
      // Nested object. We need to handle dictionaries inside it as well.
      // Most common nested object is the `custom fields` ('data' property in an example below).
      // E.g. for Visits we have the following object as data:
      // {
      //   begin: "2021-06-04T03:26:22Z"
      //   data: {txt: "dgfhdfghdfgh", checkbox: null}
      //   deviceModel: null
      //   end: "2021-06-04T03:26:24Z"
      // }
      postProcessAllDataBlockFields(
        fullPath,
        value,
        currentBaseQuery,
        postProcessFuncs
      );
    }
  });
}

export function parseDateOnly(s: string) {
  const date = new Date(s);
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
}

/*
  Function passes all dictionary values through i18n.t.
 */
function localizeDictionaryValues(value: unknown): string {
  if (Array.isArray(value)) {
    return (value as Array<any>)
      .map((x) => i18next.t(x, { ns: NS_DataBlockFields }) ?? x)
      .join(', ');
  } else {
    if (typeof value == 'string') {
      return (
        i18next.t(value as any, {
          ns: NS_DataBlockFields,
        }) ?? value
      );
    } else {
      return value as string;
    }
  }
}

/*
  Function injects `.toString()` method to all dictionary values.
  E.g. dictionary value like:
    {"id":73,"value":"Insur 123","isDeleted":false,"values":{"zxczxc":"qwerqwer","address":"asdfasdf"}}
  will now have .toString() method that will return the `value` field.
*/
function injectToStringMethodToDictionaryValues(
  row: Record<string, any>,
  key: string
) {
  const value = row[key];
  if (typeof value !== 'object') return;
  const valueArray = Array.isArray(value) ? value : [value];
  row[key] = valueArray;
  valueArray.forEach((x, index) => {
    if (typeof x === 'object' && x !== null) {
      x.toString = () => {
        return localizeDictionaryValues(x.Value);
      };
    } else if (typeof x === 'string' && x?.includes('dictionaries.')) {
      valueArray[index] = i18next.t(x as any, { ns: NS_DataBlockFields });
    }
  });
}

export function postProcessValue(dataType: DataType, row: any, key: string) {
  const value = row[key];
  if (
    dataType === DataType.Undefined ||
    dataType === DataType.Number ||
    dataType === DataType.Table ||
    ((value === null || value === undefined) && dataType !== DataType.Boolean)
  ) {
    row[key] = value;
  } else if (dataType === DataType.Dictionary) {
    injectToStringMethodToDictionaryValues(row, key);
  } else if (dataType === DataType.DateOnly) {
    if (value instanceof Date) return;
    const dateValue = parseDateOnly(
      value?.toString().replace(/T00:00:00(Z|)/, '')
    );
    if (isValidDate(dateValue)) {
      row[key] = dateValue;
      row[key].toString = () => localFormat(dateValue, 'P');
    } else {
      row[key] = null;
    }
  } else if (dataType === DataType.DateTime) {
    const dateValue = new Date(value);
    if (isValidDate(dateValue)) {
      row[key] = dateValue;
      row[key].toString = () => localFormat(dateValue, 'Pp');
    } else {
      row[key] = null;
    }
  } else if (dataType === DataType.TimeOnly) {
    const dateValue = addMilliseconds(StartDateForTimeOnlyType, value);
    row[key] = dateValue;
    row[key].toString = () => localFormat(dateValue, 'HH:mm');
  } else if (dataType === DataType.Boolean) {
    row[key] = !!value;
  } else if (
    dataType === DataType.Text ||
    dataType === DataType.MultilineText
  ) {
    row[key] = cleanTextFromTags(row[key]);
  } else {
    assertNeverDontThrow(dataType);
  }
}

function isValidDate(d: Date) {
  return d instanceof Date && !isNaN(d as any);
}

/*
  Function strips out '__id' fields from table rows and adds type fields to make it recognisable:
  [
    0: {...row1...},
    1: {...row2...},
    ...
    __type: 'raw-table',
    __columns: [columnDefinition1, columnDefinition2, etc...],
    // we can't convert it to columns/cells/etc, because it might be used in user-functions like `verticalTable`, etc
  ]
  where each '__row__' is an object, with each property/value representing a column/value
   */
export function processTables(
  queryField: DocumentQueryFieldDto,
  row: Record<string, any>,
  key: string
) {
  if (!row) return;
  if (queryField.dataType !== DataType.Table) return;
  const value = row[key];
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((tableRow) => {
      // otherwise it's impossible to edit DateTime columns in tables
      //delete tableRow['__id'];

      queryField.tableColumns?.forEach((x) => {
        postProcessValue(x.dataType, tableRow, x.name);
      });
    });
  }
  value['__type'] = 'raw-table';
  value['__columns'] = queryField.tableColumns;
}

/*
 * Calculates passed expression using passed data.
 * E.g. expression: `patient()['FirstName'] + ' ' + patient()['LastName'] + ' ' + case()['Number'] + ' ' + lastBlockInCase('PSG')['AHI']
 *      data:
 * {
 *   "patient":{
 *      "":{
 *         "FirstName":"Vasya",
 *         "LastName":"Pupkin"
 *      }
 *   },
 *   "case":{
 *      "":{
 *         "Number":"123"
 *      }
 *   },
 *   "lastBlockInCase":{
 *      "PSG":{
 *         "AHI":10
 *      }
 *   }
 * }
 *  Result: 'Vasya Pupkin 123 10'
 */
export function getExpressionFunctionCachedResult(
  dataList: ExpressionFunctionsDto[]
): ExpressionFunctionsCachedResult {
  const expressionFunctionsCachedResult = initialData();

  for (const expressionFunctionData of dataList) {
    const data = expressionFunctionData.data;
    const currentBaseQuery = expressionFunctionData.currentBaseQuery;
    const query = expressionFunctionData.query;
    if (!query) {
      continue;
    }
    const cachedResultSubkey =
      query.expressionFunctionArgument +
      (query.expressionFunctionArgument2
        ? '_' + query.expressionFunctionArgument2
        : '');
    if (!data || !currentBaseQuery) {
      expressionFunctionsCachedResult[query.expressionFunctionName][
        cachedResultSubkey
      ] = data;
      continue;
    }

    postProcessDataBlockFields(
      expressionFunctionsCachedResult,
      query.expressionFunctionName,
      cachedResultSubkey,
      data,
      currentBaseQuery
    );
  }

  return expressionFunctionsCachedResult;
}

export function postProcessDataBlockFields(
  expressionFunctionsCachedResult: ExpressionFunctionsCachedResult,
  key: ExpressionDataFunction,
  subkey: string,
  data: Record<string, any>,
  currentBaseQuery: DocumentQueryDto
) {
  try {
    postProcessAllDataBlockFields('data', data, currentBaseQuery, [
      // most probably all post-processing method added here shall be added inside `processTables` as well
      // (to process values inside tables)
      // DON'T FORGET TO ADD THEM!
      function localizeDictionaries(queryField, row, key) {
        postProcessValue(queryField.dataType, row, key);
      },
      processTables,
    ]);
    expressionFunctionsCachedResult[key][subkey] = data;
  } catch (e) {
    expressionFunctionsCachedResult[key][subkey] = 'ERROR';
    console.error('postProcessDataBlockFields', e);
  }
}
